import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {
  getAPIStatus,
  getInitialRibbonsTransactionState,
  getResponse
} from "../../../redux/slices/ribbionsTransactionSlice";
import useAppSelector from "../../../hooks/useAppSelector";
import Loader from "../../../components/loader/Loader";
import { Typography, CircularProgress } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { RIBBINGS_TOTAL_BALANCE } from "../../../utils/url";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getFormattedDate } from "../../../utils/commonFunctions";
import CopyToClipboardWrapper from "../../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import CustomDataTable from "../../../components/customTable/CustomDataTable";

const apiEnpoint = process.env.REACT_APP_RIBBIONS_API_BASE_URL;

const TransactionsList = () => {
  const { userId } = useParams<any>();
  const dispatch = useAppDispatch();
  const accessToken: any = localStorage?.getItem("accessToken");
  const responseData: any = useAppSelector(getResponse);
  const apiStatus = useAppSelector(getAPIStatus);
  const [totalBalance, setTotalBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);

  const navigate = useNavigate();

  const fetchData = (accessToken: string, userId: any) => {
    dispatch(
      getInitialRibbonsTransactionState({
        accessToken: accessToken,
        userId: userId
      })
    );
  };

  const getTotalRibbonsBalance = () => {
    let data = "";

    let config: any = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${apiEnpoint}${RIBBINGS_TOTAL_BALANCE}${userId}`,
      headers: {
        Authorization: "bearer " + accessToken,
        "Content-Type": "application/json"
      },
      data: data
    };

    axios
      .request(config)
      .then((response) => {
        setTotalBalance(response?.data?.TotalRibbonBalance);
        setLoading(false);
        setRefreshLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        setRefreshLoading(false);
      });
  };

  useEffect(() => {
    fetchData(accessToken, userId);
    getTotalRibbonsBalance();
  }, []);

  const ribbionsTransationsColumns: GridColDef[] = [
    {
      field: "TransactionId",
      headerName: "TransactionId",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 150,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <div
          style={{
            display: "flex",
            alignItems: "left"
          }}
        >
          <div
            style={{
              verticalAlign: "middle",
              display: "flex",
              alignItems: "center"
            }}
          >
            {params?.row?.TransactionId?.toString()?.split("-")[0]}
          </div>
          <div onClick={(e) => e?.stopPropagation()}>
            <CopyToClipboardWrapper
              text={params?.row?.TransactionId?.toString()}
              cell="TransactionId"
            />
          </div>
        </div>
      )
    },
    {
      field: "TransactionType",
      headerName: "Transaction Type",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 150,
      valueGetter: (params) => {
        const ribbonsEarned = params?.row?.RibbonsEarned;
        const ribbonsSpent = params?.row?.RibbonsSpent;
        const ribbonsEarnedExists = ribbonsEarned && ribbonsEarned.length > 0;
        const ribbonsSpentExists = ribbonsSpent && ribbonsSpent.length > 0;
        if (!ribbonsEarnedExists && !ribbonsSpentExists) {
          return "-";
        } else if (ribbonsEarnedExists) {
          return "Earned";
        } else {
          return "Spent";
        }
      }
    },
    {
      field: "TransactionSourceId",
      headerName: "SourceId",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 150,
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <div
          style={{
            display: "flex",
            alignItems: "left"
          }}
        >
          <div
            style={{
              verticalAlign: "middle",
              display: "flex",
              alignItems: "center"
            }}
          >
            {params?.row?.TransactionSourceId?.toString()?.split("-")[0]}
          </div>
          <div onClick={(e) => e?.stopPropagation()}>
            <CopyToClipboardWrapper
              text={params?.row?.TransactionSourceId?.toString()?.split("-")[0]}
              cell="TransactionSourceId"
            />
          </div>
        </div>
      )
    },

    {
      field: "TransactionSourceName",
      headerName: "Source Name",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 360
    },
    {
      field: "TransactionDate",
      headerName: "Transaction Date",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      renderCell: (params: GridRenderCellParams<any, any>) => (
        <>
          {params?.row?.TransactionDate
            ? getFormattedDate(params?.row?.TransactionDate)
            : "-"}
        </>
      ),
      width: 200
    },
    {
      field: "RibbonsCount",
      headerName: "Ribbons",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 120,
      valueGetter: (params) => {
        // Assuming each row has a Ribbons array
        const ribbonsEarned = params?.row["RibbonsEarned"];
        const ribbonsSpent = params?.row["RibbonsSpent"];
        if (ribbonsEarned && ribbonsEarned.length > 0) {
          return ribbonsEarned[0]?.RibbonsCount;
        } else if (ribbonsSpent && ribbonsSpent.length > 0) {
          return ribbonsSpent[0]?.RibbonsCount;
        } else {
          return "-";
        }
      }
    },
    {
      field: "RibbonsType",
      headerName: "Ribbons Type",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 150,
      valueGetter: (params) => {
        // Assuming each row has a Ribbons array
        const ribbonsEarned = params?.row["RibbonsEarned"];
        const ribbonsSpent = params?.row["RibbonsSpent"];
        if (ribbonsEarned && ribbonsEarned.length > 0) {
          return ribbonsEarned[0]?.RibbonsType;
        } else if (ribbonsSpent && ribbonsSpent.length > 0) {
          return ribbonsSpent[0]?.RibbonsType;
        } else {
          return "-";
        }
      }
    },
    {
      field: "ExpiryDate",
      headerName: "Expiry Date",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 170,
      valueGetter: (params) => {
        // Assuming each row has a Ribbons array
        const ribbonsEarned = params?.row["RibbonsEarned"];
        const ribbonsSpent = params?.row["RibbonsSpent"];
        if (ribbonsEarned && ribbonsEarned.length > 0) {
          return getFormattedDate(ribbonsEarned[0]?.ExpiryDate);
        } else if (ribbonsSpent && ribbonsSpent.length > 0) {
          return "-";
        } else {
          return "-";
        }
      }
    }
  ];

  const handleRefreshData = async () => {
    setRefreshLoading(true);
    await fetchData(accessToken, userId);
    await getTotalRibbonsBalance();
  };

  return (
    <React.Fragment>
      {responseData?.length > 0 && (
        <>
          <Typography variant="h5" mb={2}>
            {"Total Ribbons Balance: "}
            <span style={{ color: "#192f67" }}>{totalBalance}</span>
            <div style={{ float: "right", cursor: "pointer" }}>
              {refreshLoading ? (
                <CircularProgress size={25} color="inherit" />
              ) : (
                <RefreshIcon onClick={handleRefreshData} />
              )}
            </div>
          </Typography>
        </>
      )}

      {responseData?.length > 0 &&
      apiStatus !== "fetching" &&
      apiStatus !== "failed" ? (
        <CustomDataTable
          rows={responseData}
          columns={ribbionsTransationsColumns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 100 }
            }
          }}
          pageSizeOptions={[10, 50, 100]}
          getRowId={"TransactionId"}
        />
      ) : apiStatus === "fetching" || loading ? (
        <Loader />
      ) : (
        <Typography variant="h5" mb={2} align="center">
          {"No Data Available"}
        </Typography>
      )}
    </React.Fragment>
  );
};

export default TransactionsList;
